import { cn } from "@/lib/utils";
import { appRoleLabel } from "@/schemas/users";

/**
 * The permission tier badge shown on a user row.
 *
 * Colour carries the privilege level, because scanning the Users page is
 * mostly the question "who has access above a plain User?" — a list where
 * every tier is grey answers that only by reading each label.
 *
 * The two admin tiers deliberately share a hue: one is a lesser form of the
 * other, and making them unrelated colours would hide that. Filled versus
 * outlined is what separates them, which is also the visual weight difference
 * you want — full admin should be the loudest thing in the row.
 *
 * Every colour comes from the design-system tokens (which carry their own dark
 * -mode values), never a hard-coded hex.
 *
 * `role` is a raw DB string: an unrecognised value falls through to the muted
 * treatment and `appRoleLabel` renders it verbatim rather than hiding it.
 */
const TONES: Record<string, string> = {
  admin: "bg-warning-bg text-warning-foreground",
  admin_readonly: "border border-warning/50 text-warning-foreground",
  manager: "bg-info-bg text-info-foreground",
  // The default tier, and the overwhelming majority once everyone signs in —
  // it should recede so the exceptions above it carry the eye.
  user: "bg-muted text-muted-foreground",
};

export function AppRoleBadge({ role }: { role: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 font-medium text-xs",
        TONES[role] ?? TONES.user,
      )}
    >
      {appRoleLabel(role)}
    </span>
  );
}
